/* ──────────────────────────────────────────────────────────────
   src/api/prevision.ts
   API de Previsión de Profesores
   ────────────────────────────────────────────────────────────── */
import apiClient from './client';

// ═════════════════════════════════════════════════════════════
// TIPOS
// ═════════════════════════════════════════════════════════════

export type FranjaHoraria =
  | 'mañana_temprana'
  | 'mañana_media'
  | 'mediodia'
  | 'tarde_temprana'
  | 'tarde'
  | 'noche'
  | 'fin_semana';

export type DireccionAlerta = 'insuficiencia' | 'sobrecapacidad';
export type SeveridadAlerta = 'critica' | 'alta' | 'media' | 'baja';
export type EstadoAlerta = 'activa' | 'en_revision' | 'resuelta' | 'descartada';

export interface CalendarioAcademico {
  id: number;
  nombre: string;
  tipo: string;
  fechaInicio: string;
  fechaFin: string;
  factorDemanda: number;
  idTipoAsignatura?: number;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AlertaEscasez {
  id: number;
  fechaGeneracion: string;
  direccion: DireccionAlerta;
  severidad: SeveridadAlerta;
  tipoProblema: string;
  idTipoAsignatura: number;
  idCentro?: number;
  franjaHoraria?: FranjaHoraria;
  fechaInicioImpacto: string;
  fechaFinImpacto?: string;
  profesoresDiferencia: number;
  accionesRecomendadas: string[];
  profesoresFormablesDisponibles: number;
  horasFormacionEstimadas?: number;
  descripcion: string;
  estado: EstadoAlerta;
  resueltaPor?: number;
  resueltaEn?: string;
  notas?: string;
  tipoAsignatura?: {
    id: number;
    descripcion: string;
  };
  centro?: {
    id: number;
    nombre: string;
  };
}

export interface DesgloseFranja {
  franjaHoraria: FranjaHoraria;
  profesoresActivos: number;
  profesoresIds?: number[];
  porcentajeOcupacion: number | null;
  porcentajeOcupacionEfectiva?: number | null;
  horasDisponibles: number;
  horasLibres?: number;
  horasLibresUsables?: number;
  horasLibresNoUsables?: number;
  horasOcupadas?: number;
  sinCapacidad?: boolean;
  dias?: DesgloseDia[];
}

export interface DesgloseDia {
  diaSemana: string;
  profesoresActivos: number;
  profesoresIds?: number[];
  horasDisponibles: number;
  horasLibres: number;
  horasLibresUsables?: number;
  horasLibresNoUsables?: number;
  horasOcupadas: number;
  porcentajeOcupacion: number | null;
  porcentajeOcupacionEfectiva?: number | null;
  sinCapacidad: boolean;
}

export interface DesgloseCentro {
  centroId: number;
  centroNombre: string;
  profesoresActivos: number;
  profesoresIds?: number[];
  porcentajeOcupacion: number | null;
  porcentajeOcupacionEfectiva?: number | null;
  sinCapacidad?: boolean;
  franjas?: DesgloseFranja[];
}

export interface ResumenTipo {
  tipoAsignaturaId: number;
  tipoAsignaturaNombre: string;
  profesoresActivos: number;
  profesoresIds?: number[];
  porcentajeOcupacion: number | null;
  porcentajeOcupacionEfectiva?: number | null;
  demandaProyectada: number;
  desglosePorFranja?: DesgloseFranja[];
  desglosePorCentro?: DesgloseCentro[];
  sinCapacidad?: boolean;
}

export interface DashboardResponse {
  alertasPendientes: AlertaEscasez[];
  resumenPorTipo: ResumenTipo[];
}

export interface CapacidadActual {
  profesoresActivos: number;
  horasDisponibles: number;
  horasReservadas: number;
  porcentajeOcupacion: number;
}

export interface ProyeccionSemana {
  semana: number;
  demandaProyectada: number;
}

export interface ProyeccionResponse {
  capacidadActual: CapacidadActual;
  proyecciones: ProyeccionSemana[];
}

export interface AnalisisFranja {
  franjaHoraria: FranjaHoraria;
  profesoresActivos: number;
  horasDisponibles: number;
  horasReservadas: number;
  porcentajeOcupacion: number;
}

export interface AnalisisHorarioResponse {
  fecha: string;
  analisisPorFranja: AnalisisFranja[];
}

export interface ProfesorFormable {
  profesorId: number;
  nombreCompleto: string;
  tiposActuales: number[];
  planFormacion: string;
}

export interface FormabilidadResponse {
  profesoresFormables: ProfesorFormable[];
  mensaje?: string;
}

export interface AlertasResponse {
  data: AlertaEscasez[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface AsignaturaAnalisis {
  asignaturaId: number;
  asignaturaNombre: string;
  profesoresDisponibles: number;
  profesoresIds?: number[];
  horasDisponibles: number;
  horasReservadas: number;
  porcentajeOcupacion: number | null;
  porcentajeOcupacionEfectiva?: number | null;
  sinCapacidad: boolean;
  profesores: Array<{
    id: number;
    nombreCompleto: string;
  }>;
  desglosePorCentro?: DesgloseCentro[];
  desglosePorFranja?: DesgloseFranja[];
}

export interface AnalisisAsignaturasResponse {
  tipoAsignaturaId: number;
  totalAsignaturas: number;
  fecha?: string;
  asignaturas: AsignaturaAnalisis[];
}

// ═════════════════════════════════════════════════════════════
// ENDPOINTS
// ═════════════════════════════════════════════════════════════

const BASE_URL = '/prevision-profesores';

/**
 * Dashboard con alertas pendientes y resumen por tipo
 */
export async function getDashboard(params?: {
  centroId?: number;
  mesesAdelante?: number;
  fecha?: string;
}): Promise<DashboardResponse> {
  return await apiClient.get<DashboardResponse>(`${BASE_URL}/dashboard`, {
    params,
  });
}

/**
 * Proyección de demanda y capacidad para un tipo de asignatura
 */
export async function getProyeccion(params: {
  tipoId: number;
  centroId?: number;
  franjaHoraria?: FranjaHoraria;
}): Promise<ProyeccionResponse> {
  return await apiClient.get<ProyeccionResponse>(`${BASE_URL}/proyeccion`, {
    params,
  });
}

/**
 * Análisis por franja horaria
 */
export async function getAnalisisHorario(params: {
  tipoId: number;
  centroId?: number;
  fecha?: string;
}): Promise<AnalisisHorarioResponse> {
  return await apiClient.get<AnalisisHorarioResponse>(
    `${BASE_URL}/analisis-horario`,
    { params }
  );
}

/**
 * Profesores formables en un tipo de asignatura
 */
export async function getFormabilidad(params: {
  tipoId: number;
}): Promise<FormabilidadResponse> {
  return await apiClient.get<FormabilidadResponse>(`${BASE_URL}/formabilidad`, {
    params,
  });
}

/**
 * Lista de alertas con filtros
 */
export async function getAlertas(params?: {
  estado?: EstadoAlerta;
  severidad?: SeveridadAlerta;
  page?: number;
  limit?: number;
}): Promise<AlertasResponse> {
  return await apiClient.get<AlertasResponse>(`${BASE_URL}/alertas`, {
    params,
  });
}

/**
 * Resolver una alerta
 */
export async function resolverAlerta(
  id: number,
  payload: { notas: string }
): Promise<AlertaEscasez> {
  return await apiClient.patch<AlertaEscasez>(
    `${BASE_URL}/alertas/${id}/resolver`,
    payload
  );
}

/**
 * Descartar una alerta
 */
export async function descartarAlerta(
  id: number,
  payload: { motivo: string }
): Promise<AlertaEscasez> {
  return await apiClient.patch<AlertaEscasez>(
    `${BASE_URL}/alertas/${id}/descartar`,
    payload
  );
}

/**
 * Listar eventos del calendario académico
 */
export async function getCalendario(): Promise<CalendarioAcademico[]> {
  return await apiClient.get<CalendarioAcademico[]>(`${BASE_URL}/calendario`);
}

/**
 * Crear evento en calendario académico
 */
export async function createEventoCalendario(
  payload: Omit<CalendarioAcademico, 'id' | 'createdAt' | 'updatedAt'>
): Promise<CalendarioAcademico> {
  return await apiClient.post<CalendarioAcademico>(
    `${BASE_URL}/calendario`,
    payload
  );
}

/**
 * Actualizar evento del calendario
 */
export async function updateEventoCalendario(
  id: number,
  payload: Partial<Omit<CalendarioAcademico, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<CalendarioAcademico> {
  return await apiClient.patch<CalendarioAcademico>(
    `${BASE_URL}/calendario/${id}`,
    payload
  );
}

/**
 * Eliminar evento del calendario
 */
export async function deleteEventoCalendario(id: number): Promise<void> {
  await apiClient.del(`${BASE_URL}/calendario/${id}`);
}

/**
 * Análisis de ocupación por asignatura individual
 */
export async function getAnalisisAsignaturas(
  tipoId: number,
  centroId?: number,
  fecha?: string
): Promise<AnalisisAsignaturasResponse> {
  return await apiClient.get<AnalisisAsignaturasResponse>(
    `${BASE_URL}/analisis-asignaturas/${tipoId}`,
    { params: { centroId, fecha } }
  );
}

export default {
  getDashboard,
  getProyeccion,
  getAnalisisHorario,
  getFormabilidad,
  getAlertas,
  resolverAlerta,
  descartarAlerta,
  getCalendario,
  createEventoCalendario,
  updateEventoCalendario,
  deleteEventoCalendario,
};
